import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ListOrdered, RefreshCw, XCircle, RotateCcw, Trash2, Eye, Bot, MonitorSmartphone, ArrowLeft, Users, Pause, Play, Activity, CheckCircle2, Clock, Eraser, Zap, History, GripVertical, ArrowUpToLine, Link2 } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard, PageHeader, Spinner, EmptyState, Modal } from "../components/common";
import { DomainGate } from "../components/DomainGate";
import { useLang } from "../context/LanguageContext";

const STATUS = {
  queued: { label: "Na fila", dot: "bg-amber-400", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  running: { label: "Importando", dot: "bg-cyan-400 animate-pulse", cls: "text-cyan-300 border-cyan-400/40 bg-cyan-400/10" },
  done: { label: "Concluído", dot: "bg-emerald-400", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  failed: { label: "Erro", dot: "bg-rose-400", cls: "text-rose-300 border-rose-400/40 bg-rose-400/10" },
  canceled: { label: "Cancelado", dot: "bg-slate-500", cls: "text-slate-400 border-slate-600 bg-slate-800/40" },
  paused: { label: "Pausado", dot: "bg-violet-400", cls: "text-violet-300 border-violet-400/40 bg-violet-400/10" },
};

const fmtDur = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}min`;
};
const fmtSpeed = (b) => (b == null ? "—" : `${(b / 1e6).toFixed(1)} MB/s`);
const fmtDT = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function Fila() {
  const { user } = useAuth();
  const { t } = useLang();
  const isAdmin = user.role === "super_admin";
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [clientFilter, setClientFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [page, setPage] = useState(1);
  const [errPage, setErrPage] = useState(1);

  const load = useCallback(() => {
    api.get("/queue", { params: clientFilter ? { user_id: clientFilter } : {} })
      .then((r) => setItems(r.data.items))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/queue/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [statusFilter, clientFilter]);
  useEffect(() => {
    const t = setInterval(() => {
      if ((items || []).some((i) => i.status === "running" || i.status === "queued")) load();
    }, 5000);
    return () => clearInterval(t);
  }, [items, load]);

  const act = async (key, fn, msg) => {
    setBusy(key);
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };

  const openDetail = async (it) => {
    setErrPage(1);
    if (it.kind === "upload") { setDetail({ upload: it }); return; }
    try {
      const { data } = await api.get(`/queue/jobs/${it.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [hist, setHist] = useState({});

  const toggleHist = async (it) => {
    const cur = hist[it.id];
    if (cur?.open) {
      setHist({ ...hist, [it.id]: { ...cur, open: false } });
      return;
    }
    setHist({ ...hist, [it.id]: { open: true, data: cur?.data || null } });
    if (!cur?.data) {
      try {
        const { data } = await api.get(`/queue/jobs/${it.id}`);
        setHist((h) => ({ ...h, [it.id]: { open: true, data } }));
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const [dragId, setDragId] = useState(null);

  const onDropJob = async (targetId) => {
    const drag = dragId;
    setDragId(null);
    if (!drag || drag === targetId) return;
    const queued = (items || []).filter((i) => i.status === "queued" && i.kind !== "upload");
    const ids = queued.map((i) => i.id);
    const from = ids.indexOf(drag);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    await applyOrder(ids);
  };

  const applyOrder = async (ids) => {
    const order = Object.fromEntries(ids.map((id, idx) => [id, idx]));
    setItems((items || []).map((i) => (order[i.id] !== undefined ? { ...i, _ord: order[i.id] } : i)));
    try {
      await api.post("/queue/jobs/reorder", { job_ids: ids });
      toast.success("Prioridade atualizada — a fila segue a nova ordem");
    } catch (e) { toast.error(formatApiError(e)); }
    load();
  };

  const moveToTop = async (it) => {
    const queued = (items || [])
      .filter((i) => i.status === "queued" && i.kind !== "upload")
      .sort((a, b) => (a._ord ?? a.prio ?? 9e12) - (b._ord ?? b.prio ?? 9e12));
    if (!queued.length || queued[0].id === it.id) return;
    await applyOrder([it.id, ...queued.map((q) => q.id).filter((id) => id !== it.id)]);
    toast.success(`"${it.name}" passou a 1º da fila — entra assim que abrir vaga`);
  };

  const refreshLinks = async (it) => {
    setBusy(`rf-${it.id}`);
    try {
      const { data } = await api.post(`/import/jobs/${it.id}/refresh-links`);
      toast.success(data.updated
        ? `${data.updated} link(s) renovados da origem — ${data.requeued} item(ns) voltaram à fila`
        : "Lista relida — itens com erro voltaram à fila");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
    load();
  };

  const resumeRef = useRef(null);
  const [resumeTarget, setResumeTarget] = useState(null);

  const onResumeFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const it = resumeTarget;
    setResumeTarget(null);
    if (!file || !it) return;
    if (file.name !== it.name || file.size !== it.size_bytes) {
      toast.error(`Ficheiro errado — escolhe exatamente "${it.name}" (${fmtBytes(it.size_bytes)}) para continuar este upload`);
      return;
    }
    setBusy(`up-${it.id}`);
    try {
      // a sessão ativa é reutilizada pelo backend (mesmo ficheiro/tamanho) — partes já enviadas são saltadas
      const { data: sess } = await api.post("/uploads/init", {
        bot_id: it.bot_id, filename: it.name, size_bytes: it.size_bytes,
        folder: it.folder || null, title_id: it.title_id || undefined,
        season: it.season || undefined, episode: it.episode || undefined,
      });
      const totalParts = Math.ceil(it.size_bytes / sess.chunk_size);
      const doneParts = new Set(sess.parts_done || []);
      for (let n = 1; n <= totalParts; n++) {
        if (doneParts.has(n)) continue;
        const blob = file.slice((n - 1) * sess.chunk_size, n * sess.chunk_size);
        await api.put(`/uploads/${sess.session_id}/part/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
      }
      await api.post(`/uploads/${sess.session_id}/finish`);
      toast.success(`"${it.name}" terminou de subir e já está no acervo`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
      load();
    }
  };

  const RANK = { running: 0, queued: 1, failed: 2, paused: 3 };
  const visible = (items || [])
    .filter((i) => !statusFilter || i.status === statusFilter)
    .sort((a, b) => ((RANK[a.status] ?? 4) - (RANK[b.status] ?? 4)) || ((a._ord ?? a.prio ?? 9e12) - (b._ord ?? b.prio ?? 9e12)));
  // posição numerada (1º, 2º, 3º…) entre os processos "Na fila", pela prioridade real
  const queuePos = {};
  (items || [])
    .filter((i) => i.status === "queued" && i.kind !== "upload")
    .sort((a, b) => (a._ord ?? a.prio ?? 9e12) - (b._ord ?? b.prio ?? 9e12))
    .forEach((i, idx) => { queuePos[i.id] = idx + 1; });
  const counts = (items || []).reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});
  const finishedCount = (counts.done || 0) + (counts.failed || 0) + (counts.canceled || 0);
  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const clientName = clientFilter && summary?.clients?.find((c) => c.user_id === clientFilter);

  return (
    <div className="space-y-6" data-testid="fila-page">
      <PageHeader
        eyebrow="Central"
        title={isAdmin ? t("f_title_admin") : t("f_title")}
        subtitle={t("f_sub")}
      />
      <DomainGate />
      <input ref={resumeRef} type="file" className="hidden" data-testid="fila-resume-input" onChange={onResumeFile} />

      {items !== null && items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5" data-testid="fila-stats">
          {[
            ["running", "Importando", Activity, "text-cyan-300"],
            ["queued", "Na fila", Clock, "text-amber-300"],
            ["paused", "Pausados", Pause, "text-violet-300"],
            ["done", "Concluídos", CheckCircle2, "text-emerald-300"],
            ["failed", "Erros", XCircle, "text-rose-300"],
          ].map(([k, label, Icon, iconCls]) => (
            <button key={k} data-testid={`fila-stat-${k}`} onClick={() => setStatusFilter(statusFilter === k ? "" : k)}
              className={`rounded-xl border bg-slate-950/50 p-3 text-left transition-colors hover:bg-slate-900/60 ${statusFilter === k ? "border-cyan-400/50" : "border-slate-800/60"}`}>
              <Icon size={14} className={iconCls} />
              <p className="font-display text-lg font-bold text-white mt-1.5">{counts[k] || 0}</p>
              <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500">{label}</p>
            </button>
          ))}
        </div>
      )}

      {isAdmin && summary && summary.clients.length > 0 && (
        <div className="space-y-3" data-testid="fila-clients">
          {clientFilter && (
            <button data-testid="fila-clear-client" onClick={() => setClientFilter(null)} className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5">
              <ArrowLeft size={13} /> Todos os clientes
            </button>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.clients.map((c) => (
              <GlassCard
                key={c.user_id || "none"}
                className={`p-4 cursor-pointer transition-colors ${clientFilter === c.user_id ? "border-cyan-400/50" : "hover:border-slate-700"}`}
                data-testid={`fila-client-${c.user_id}`}
                onClick={() => setClientFilter(c.user_id)}
              >
                <p className="text-sm font-semibold text-white truncate flex items-center gap-2">
                  <Users size={13} className="text-cyan-400 shrink-0" /> {c.name || c.email || "Sem vínculo"}
                </p>
                <p className="text-[10px] font-mono2 text-slate-500 truncate mb-2">{c.email}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono2">
                  <span className="text-emerald-300">{c.done} concluídos</span>
                  <span className="text-cyan-300">{c.running} importando</span>
                  <span className="text-amber-300">{c.queued} aguardando</span>
                  <span className={c.failed ? "text-rose-300" : "text-slate-600"}>{c.failed} erros</span>
                </div>
              </GlassCard>
            ))}
          </div>
          {clientName && <p className="text-xs text-slate-400">Fila individual de <span className="text-white font-semibold">{clientName.name || clientName.email}</span></p>}
        </div>
      )}

      {!isAdmin && summary?.mine && (
        <div className="flex flex-wrap gap-3 text-[11px] font-mono2" data-testid="fila-my-summary">
          <span className="text-cyan-300">{summary.mine.running} importando</span>
          <span className="text-amber-300">{summary.mine.queued} aguardando</span>
          <span className="text-emerald-300">{summary.mine.done} concluídos</span>
          <span className={summary.mine.failed ? "text-rose-300" : "text-slate-600"}>{summary.mine.failed} erros</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="fila-status-filters">
        {[["", "Todos"], ["running", "Importando"], ["queued", "Na fila"], ["paused", "Pausados"], ["done", "Concluídos"], ["failed", "Erros"], ["canceled", "Cancelados"]].map(([v, l]) => (
          <button key={v} data-testid={`fila-filter-${v || "all"}`} onClick={() => setStatusFilter(v)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${statusFilter === v ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" : "text-slate-400 border-slate-800 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {finishedCount > 0 && (
        <div className="flex justify-end -mt-3" data-testid="fila-clear-row">
          <button data-testid="fila-clear-finished"
            onClick={() => setConfirm({ title: "Limpar histórico", text: `Remover ${finishedCount} processo(s) finalizados (concluídos, erros e cancelados) da lista? Processos em andamento, na fila ou pausados não são tocados.`, fn: () => act("clear", () => api.post("/queue/jobs/clear-finished", null, { params: clientFilter ? { user_id: clientFilter } : {} }), "Histórico limpo") })}
            className="text-[11px] font-semibold text-slate-500 hover:text-rose-300 inline-flex items-center gap-1.5 transition-colors">
            <Eraser size={13} /> Limpar finalizados ({finishedCount})
          </button>
        </div>
      )}

      {items === null ? (
        <div className="flex justify-center py-14"><Spinner /></div>
      ) : visible.length === 0 ? (
        <EmptyState icon={ListOrdered} title={t("f_empty")} subtitle={t("f_empty_sub")} testid="fila-empty" />
      ) : (
        <div className="space-y-2" data-testid="fila-list">
          {pageItems.map((it) => {
            const st = STATUS[it.status] || STATUS.done;
            const active = it.status === "running" || it.status === "queued";
            return (
              <GlassCard key={`${it.kind}-${it.id}`} className={`px-4 py-3 transition-opacity ${dragId === it.id ? "opacity-40 border-cyan-400/50" : ""}`} data-testid={`fila-item-${it.id}`}
                draggable={it.status === "queued" && it.kind !== "upload"}
                onDragStart={() => setDragId(it.id)}
                onDragOver={(e) => { if (dragId && dragId !== it.id && it.status === "queued" && it.kind !== "upload") e.preventDefault(); }}
                onDrop={() => onDropJob(it.id)}
                onDragEnd={() => setDragId(null)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  {it.status === "queued" && it.kind !== "upload" && (
                    <span className="inline-flex items-center gap-1 shrink-0 -ml-1" data-testid={`fila-pos-${it.id}`} title="Posição na fila — arraste para reordenar">
                      <GripVertical size={13} className="text-slate-600 cursor-grab" data-testid={`fila-grip-${it.id}`} />
                      <span className="text-[10px] font-mono2 font-bold text-amber-300 bg-amber-400/10 border border-amber-400/25 rounded px-1.5 py-0.5">{queuePos[it.id]}º</span>
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-mono2 font-semibold uppercase tracking-wider ${st.cls}`} data-testid={`fila-status-${it.id}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {st.label}
                  </span>
                  {it.kind === "upload" && it.stalled && (
                    <span className="text-[9px] font-mono2 uppercase tracking-widest text-amber-300 border border-amber-400/40 bg-amber-400/10 rounded px-1.5 py-0.5 animate-pulse" data-testid={`fila-stalled-${it.id}`} title="Sem progresso há vários minutos — usa o botão Retomar e escolhe o mesmo ficheiro">Travado</span>
                  )}
                  {it.kind !== "upload" && it.stalled && it.status === "queued" && (
                    <span className="text-[9px] font-mono2 uppercase tracking-widest text-emerald-300 border border-emerald-400/40 bg-emerald-400/10 rounded px-1.5 py-0.5" data-testid={`fila-stalled-${it.id}`} title="Este processo estava travado e foi recolocado na fila automaticamente">Retomado autom.</span>
                  )}
                  <span className="text-[10px] font-mono2 text-slate-500 inline-flex items-center gap-1" data-testid={`fila-origin-${it.id}`}>
                    {it.origin === "telegram" ? <><Bot size={11} /> Telegram</> : <><MonitorSmartphone size={11} /> Painel Web</>}
                  </span>
                  <span className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500 border border-slate-800 rounded px-1.5 py-0.5 shrink-0" data-testid={`fila-kind-${it.id}`}>{it.label}</span>
                  <p className="text-xs font-semibold text-white truncate flex-1 min-w-40" data-testid={`fila-name-${it.id}`}>{it.name}</p>
                  {isAdmin && it.owner_email && <span className="text-[10px] font-mono2 text-violet-300/80 truncate">{it.owner_name || it.owner_email}</span>}
                  <div className="flex items-center gap-1">
                    <button data-testid={`fila-detail-${it.id}`} title="Detalhes" onClick={() => openDetail(it)} className="p-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-white"><Eye size={13} /></button>
                    <button data-testid={`fila-history-${it.id}`} title="Histórico completo desta importação" onClick={() => toggleHist(it)} className={`p-1.5 rounded-lg btn-ghost-dark ${hist[it.id]?.open ? "text-cyan-300" : "text-slate-400 hover:text-white"}`}><History size={13} /></button>
                    {active && it.kind !== "upload" && (
                      <>
                        {it.status === "queued" && (
                          <>
                            <button data-testid={`fila-top-${it.id}`} title="Mover para o TOPO — passa a 1º da fila e entra assim que abrir vaga" disabled={busy === `t-${it.id}` || queuePos[it.id] === 1}
                              onClick={() => moveToTop(it)}
                              className="p-1.5 rounded-lg btn-ghost-dark text-cyan-300 disabled:opacity-40"><ArrowUpToLine size={13} /></button>
                            <button data-testid={`fila-force-${it.id}`} title="Forçar início AGORA — fura a fila e o limite de simultâneas" disabled={busy === `f-${it.id}`}
                              onClick={() => act(`f-${it.id}`, () => api.post(`/queue/jobs/${it.id}/force`), "Início forçado")}
                              className="p-1.5 rounded-lg btn-ghost-dark text-amber-300"><Zap size={13} /></button>
                          </>
                        )}
                        {it.status === "running" && (
                          <button data-testid={`fila-kick-${it.id}`} title="Parece travado? Retomar — volta à fila AGORA mantendo tudo o que já foi publicado" disabled={busy === `k-${it.id}`}
                            onClick={() => act(`k-${it.id}`, () => api.post(`/queue/jobs/${it.id}/kick`), "Processo retomado — o progresso publicado manteve-se")}
                            className="p-1.5 rounded-lg btn-ghost-dark text-emerald-300"><Play size={13} /></button>
                        )}
                        <button data-testid={`fila-pause-${it.id}`} title="Pausar (retomável depois)" disabled={busy === `p-${it.id}`}
                          onClick={() => act(`p-${it.id}`, () => api.post(`/queue/jobs/${it.id}/pause`), "Processo pausado")}
                          className="p-1.5 rounded-lg btn-ghost-dark text-violet-300"><Pause size={13} /></button>
                        <button data-testid={`fila-cancel-${it.id}`} title="Cancelar" disabled={busy === `c-${it.id}`}
                          onClick={() => setConfirm({ title: "Cancelar processo", text: `Cancelar "${it.name}"? O item em andamento termina e a fila para.`, fn: () => act(`c-${it.id}`, () => api.post(`/queue/jobs/${it.id}/cancel`), "Processo cancelado") })}
                          className="p-1.5 rounded-lg btn-ghost-dark text-amber-300"><XCircle size={13} /></button>
                      </>
                    )}
                    {it.status === "paused" && it.kind !== "upload" && (
                      <>
                        <button data-testid={`fila-force-${it.id}`} title="Forçar início AGORA — fura a fila e o limite de simultâneas" disabled={busy === `f-${it.id}`}
                          onClick={() => act(`f-${it.id}`, () => api.post(`/queue/jobs/${it.id}/force`), "Início forçado")}
                          className="p-1.5 rounded-lg btn-ghost-dark text-amber-300"><Zap size={13} /></button>
                        <button data-testid={`fila-resume-${it.id}`} title="Retomar (continua de onde parou)" disabled={busy === `s-${it.id}`}
                          onClick={() => act(`s-${it.id}`, () => api.post(`/queue/jobs/${it.id}/resume`), "Processo retomado")}
                          className="p-1.5 rounded-lg btn-ghost-dark text-emerald-300"><Play size={13} /></button>
                        <button data-testid={`fila-cancel-${it.id}`} title="Cancelar" disabled={busy === `c-${it.id}`}
                          onClick={() => setConfirm({ title: "Cancelar processo", text: `Cancelar "${it.name}"? Essa ação não pode ser desfeita.`, fn: () => act(`c-${it.id}`, () => api.post(`/queue/jobs/${it.id}/cancel`), "Processo cancelado") })}
                          className="p-1.5 rounded-lg btn-ghost-dark text-amber-300"><XCircle size={13} /></button>
                      </>
                    )}
                    {active && it.kind === "upload" && (
                      <>
                        <button data-testid={`fila-resume-${it.id}`} title={it.stalled ? "Upload TRAVADO — Retomar: escolhe o mesmo ficheiro no PC e ele continua de onde parou, para o mesmo destino" : "Retomar upload — escolhe o mesmo ficheiro no PC e ele continua de onde parou"} disabled={busy === `up-${it.id}`}
                          onClick={() => { setResumeTarget(it); resumeRef.current?.click(); }}
                          className={`p-1.5 rounded-lg btn-ghost-dark ${it.stalled ? "text-amber-300 animate-pulse" : "text-emerald-300"}`}>
                          {busy === `up-${it.id}` ? <Spinner /> : <Play size={13} />}
                        </button>
                        <button data-testid={`fila-cancel-${it.id}`} title="Cancelar upload" disabled={busy === `c-${it.id}`}
                          onClick={() => setConfirm({ title: "Cancelar upload", text: `Cancelar o upload de "${it.name}"? As partes já enviadas são descartadas.`, fn: () => act(`c-${it.id}`, () => api.delete(`/queue/uploads/${it.id}`), "Upload cancelado") })}
                          className="p-1.5 rounded-lg btn-ghost-dark text-amber-300"><XCircle size={13} /></button>
                      </>
                    )}
                    {!active && it.kind !== "upload" && (
                      <>
                        {(it.status === "failed" || (it.errors?.length ?? 0) > 0) && (
                          <button data-testid={`fila-refresh-${it.id}`} title="Renovar links da origem — relê a lista M3U e substitui só os links falhados/expirados" disabled={busy === `rf-${it.id}`}
                            onClick={() => refreshLinks(it)}
                            className="p-1.5 rounded-lg btn-ghost-dark text-cyan-300">{busy === `rf-${it.id}` ? <Spinner /> : <Link2 size={13} />}</button>
                        )}
                        <button data-testid={`fila-retry-${it.id}`} title="Tentar novamente (continua do que falta)" disabled={busy === `r-${it.id}`}
                          onClick={() => act(`r-${it.id}`, () => api.post(`/queue/jobs/${it.id}/retry`), "Processo recolocado na fila")}
                          className="p-1.5 rounded-lg btn-ghost-dark text-emerald-300"><RefreshCw size={13} /></button>
                        <button data-testid={`fila-restart-${it.id}`} title="Recomeçar do zero" disabled={busy === `z-${it.id}`}
                          onClick={() => setConfirm({ title: "Recomeçar do zero", text: `Isso limpa todo o progresso de "${it.name}" e inicia a importação desde o começo. Confirmar?`, fn: () => act(`z-${it.id}`, () => api.post(`/queue/jobs/${it.id}/restart`), "Processo reiniciado do zero") })}
                          className="p-1.5 rounded-lg btn-ghost-dark text-cyan-300"><RotateCcw size={13} /></button>
                        <button data-testid={`fila-remove-${it.id}`} title="Remover da fila" disabled={busy === `d-${it.id}`}
                          onClick={() => act(`d-${it.id}`, () => api.delete(`/queue/jobs/${it.id}`), "Removido da fila")}
                          className="p-1.5 rounded-lg btn-ghost-dark text-rose-400"><Trash2 size={13} /></button>
                      </>
                    )}
                  </div>
                </div>
                {["running", "queued", "paused"].includes(it.status) && it.current && (
                  <p className="text-[11px] text-cyan-300/90 mt-2 truncate" data-testid={`fila-current-${it.id}`}>{it.current}</p>
                )}
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-1.5 flex-1 rounded-full bg-slate-800 overflow-hidden" data-testid={`fila-progress-${it.id}`}>
                    <div className={`h-full ${it.status === "failed" ? "bg-rose-400" : "bg-cyan-400"}`} style={{ width: `${it.pct}%` }} />
                  </div>
                  <span className="text-[10px] font-mono2 text-slate-400 shrink-0">{it.pct}%</span>
                </div>
                <p className="text-[10px] font-mono2 text-slate-500 mt-1.5 truncate" data-testid={`fila-meta-${it.id}`}>
                  {it.total > 1 ? `${it.processed}/${it.total} processados · faltam ${it.remaining} · ` : ""}
                  {it.kind === "migrate" && it.published > 0 ? `${it.published} copiados · ` : ""}
                  {it.kind === "migrate" && it.skipped > 0 ? `${it.skipped} já existiam · ` : ""}
                  {it.kind === "migrate" && it.total >= 1000 && it.status === "running" ? <span className="text-amber-400/90">total parcial — a listar mais objetos · </span> : null}
                  {it.size_bytes ? `${fmtBytes(it.size_bytes)} · ` : ""}
                  {fmtSpeed(it.speed_bps)} · {fmtDur(it.elapsed_s)} · início {fmtDT(it.started_at)} · id {String(it.id).slice(0, 8)}
                </p>
                {it.error && <p className="text-[11px] text-rose-300 mt-1" data-testid={`fila-error-${it.id}`}>⚠ {it.error}</p>}
                {!it.error && it.errors?.length > 0 && <p className="text-[10px] text-amber-300/80 mt-1 truncate font-mono2">Último erro: {it.errors[it.errors.length - 1]}</p>}
                {hist[it.id]?.open && (
                  <div className="mt-2 rounded-xl border border-slate-800 bg-slate-900/60 p-3 max-h-48 overflow-y-auto" data-testid={`fila-history-panel-${it.id}`}>
                    {!hist[it.id]?.data ? (
                      <div className="flex justify-center py-2"><Spinner /></div>
                    ) : (
                      <div className="space-y-1.5">
                        {(hist[it.id].data.history || []).slice().reverse().map((h, i) => (
                          <p key={`h${i}`} className="text-[10px] font-mono2"><span className="text-slate-600">{fmtDT(h.at)}</span> <span className="text-slate-300">{h.text}</span></p>
                        ))}
                        {(hist[it.id].data.errors || []).length > 0 && (
                          <div className="pt-1.5 border-t border-slate-800/60 space-y-1">
                            <p className="text-[9px] font-mono2 uppercase tracking-widest text-rose-400/80">Erros ({hist[it.id].data.errors.length})</p>
                            {hist[it.id].data.errors.slice(-10).reverse().map((e, i) => (
                              <p key={`e${i}`} className="text-[10px] font-mono2 text-rose-300/80 break-all">{e}</p>
                            ))}
                          </div>
                        )}
                        {!(hist[it.id].data.history || []).length && !(hist[it.id].data.errors || []).length && (
                          <p className="text-[10px] text-slate-600">Sem eventos registados.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}

      {visible.length > PAGE_SIZE && (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="fila-pagination">
          <span className="text-[11px] font-mono2 text-slate-500">Página {safePage} de {totalPages} · {visible.length} processos</span>
          <div className="flex items-center gap-1.5">
            <button data-testid="fila-page-prev" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} className="px-3 py-1.5 rounded-lg text-[11px] border border-slate-800 text-slate-400 hover:text-white disabled:opacity-30">← Anterior</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2).map((p, idx, arr) => (
              <span key={p} className="flex items-center gap-1.5">
                {idx > 0 && arr[idx - 1] < p - 1 && <span className="text-slate-600 text-[11px]">…</span>}
                <button data-testid={`fila-page-${p}`} onClick={() => setPage(p)} className={`w-8 h-8 rounded-lg text-[11px] font-mono2 ${p === safePage ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-500 hover:text-white border border-slate-800"}`}>{p}</button>
              </span>
            ))}
            <button data-testid="fila-page-next" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} className="px-3 py-1.5 rounded-lg text-[11px] border border-slate-800 text-slate-400 hover:text-white disabled:opacity-30">Próxima →</button>
          </div>
        </div>
      )}

      <Modal open={!!confirm} onClose={() => setConfirm(null)} title={confirm?.title || "Confirmar"} testid="fila-confirm-modal">
        {confirm && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300" data-testid="fila-confirm-text">{confirm.text}</p>
            <div className="flex gap-3">
              <button data-testid="fila-confirm-ok" onClick={confirm.fn} className="flex-1 py-3 rounded-xl text-sm font-semibold bg-rose-500/20 border border-rose-400/40 text-rose-300 hover:bg-rose-500/30 transition-colors">Confirmar</button>
              <button data-testid="fila-confirm-cancel" onClick={() => setConfirm(null)} className="btn-ghost-dark px-5 py-3 rounded-xl text-sm text-slate-300">Voltar</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Detalhes do processo" testid="fila-detail-modal">
        {detail && (
          <div className="space-y-3 text-xs font-mono2" data-testid="fila-detail-body">
            {(detail.upload ? Object.entries(detail.upload) : Object.entries(detail)).filter(([k, v]) => !["errors", "_id"].includes(k) && v !== null && typeof v !== "object").map(([k, v]) => (
              <p key={k} className="flex justify-between gap-3 border-b border-slate-800/40 pb-1.5">
                <span className="text-slate-500">{k}</span>
                <span className="text-slate-200 text-right break-all max-w-[65%]">{String(v)}</span>
              </p>
            ))}
            {(detail.errors || []).length > 0 && (() => {
              const errs = detail.errors;
              const ePages = Math.max(1, Math.ceil(errs.length / 10));
              const ePage = Math.min(errPage, ePages);
              const slice = errs.slice((ePage - 1) * 10, ePage * 10);
              return (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-slate-500">erros ({errs.length}) · página {ePage}/{ePages}</p>
                    {ePages > 1 && (
                      <div className="flex gap-1.5">
                        <button data-testid="fila-err-prev" disabled={ePage <= 1} onClick={() => setErrPage(ePage - 1)} className="px-2 py-1 rounded border border-slate-800 text-slate-400 disabled:opacity-30">←</button>
                        <button data-testid="fila-err-next" disabled={ePage >= ePages} onClick={() => setErrPage(ePage + 1)} className="px-2 py-1 rounded border border-slate-800 text-slate-400 disabled:opacity-30">→</button>
                      </div>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1" data-testid="fila-errors-list">
                    {slice.map((e, i) => <p key={(ePage - 1) * 10 + i} className="text-rose-300/90 break-all">{e}</p>)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
}
